import { ACCOUNT_CARDS_GRID_ID } from "testIds";
import React, { useContext } from "react";
import { ConsolidatedAccountData } from "@shared/types";
import AccountCard from "features/portfolio/Accounts/AccountCard";
import EqualColumnsGrid from "common/Grids/EqualColumnsGrid";
import useMeasure from "utils/hooks/useMeasure";
import { useBottomNavigator } from "utils/hooks/misc";
import FlatList from "common/Lists/FlatList";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { AccountsPageContext } from "features/portfolio/Accounts/AccountsPage.context";
import { useTheme } from "styled-components";
import NewLookAccountCard, {
  NewLookAccountCardSkeleton,
} from "features/portfolio/NewLook/NewLookAccountCard.tsx";
import { NewLookCardsGrid } from "features/portfolio/NewLook/styles.ts";

type ConsolidatedAccountDataWithKey = ConsolidatedAccountData & {
  key: string | number;
};
interface AccountsCardsListInterface {
  data: ConsolidatedAccountDataWithKey[];
  loading: boolean;
  accountLoading: boolean;
  openAccountExtraDetails?: (accountData: ConsolidatedAccountData) => void;
  emptyState?: React.JSX.Element;
  renderTopContainer?: () => React.JSX.Element;
}

const AccountsCardsList = ({
  data,
  loading,
  accountLoading,
  openAccountExtraDetails,
  emptyState,
  renderTopContainer,
}: AccountsCardsListInterface) => {
  const theme = useTheme();
  const [measureRef, { width }] = useMeasure<HTMLDivElement>();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { isFetching, refetch } = useContext(AccountsPageContext)!;

  const minCardWidth = 360;
  const numCardsInRow = Math.floor(width / minCardWidth);

  const renderContent = ({
    item,
  }: {
    item: ConsolidatedAccountDataWithKey;
  }) => {
    if (theme.isNewLook) {
      return (
        <NewLookAccountCard
          key={item?.client_id}
          account={item}
          loading={accountLoading}
          openAccountExtraDetails={openAccountExtraDetails}
        />
      );
    }
    return (
      <AccountCard
        key={item?.client_id}
        account={item}
        loading={accountLoading}
        openAccountExtraDetails={openAccountExtraDetails}
      />
    );
  };

  if (theme.isNewLook && !isBottomNavigator) {
    if (!data.length && !loading) return emptyState ?? null;
    return (
      <NewLookCardsGrid
        $minWidth={360}
        $tabletMinWidth={320}
        data-testid={ACCOUNT_CARDS_GRID_ID}
      >
        {!data.length
          ? [0, 1, 2].map((index) => <NewLookAccountCardSkeleton key={index} />)
          : data.map((item) => renderContent({ item }))}
      </NewLookCardsGrid>
    );
  }

  const content = isBottomNavigator ? (
    <FlatList
      data={data}
      loading={loading}
      renderContent={renderContent}
      renderTopContainer={renderTopContainer}
      refreshing={isFetching}
      onRefresh={refetch}
      emptyState={emptyState}
      hasActionButton={true}
    />
  ) : data.length || loading ? (
    <LoadingSpinnerOverlay
      isLoading={loading}
      backgroundColor={theme.colors.cardBgColor}
    >
      <EqualColumnsGrid numColumns={numCardsInRow}>
        {data.map((item) => renderContent({ item }))}
      </EqualColumnsGrid>
    </LoadingSpinnerOverlay>
  ) : (
    emptyState
  );

  return isBottomNavigator ? (
    content
  ) : (
    <div ref={measureRef}>{!!width && content}</div>
  );
};

export default AccountsCardsList;
